import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { theme } from '../theme'; // Import your theme
import { rgba } from 'polished';
import { Flex } from './layout';

// Define styles for the chip
const ChipStyle = styled(Flex)((props) => {
  const { variant, color, size, sx,noHover } = props;

  const chipSize = {
    sm: '1.5rem',
    md: '2rem',
    lg: '2.5rem',
  }[size];

  const backgroundColor = {
    filled: rgba(theme.colors[color]?.main, 0.05) || theme.colors.default.main,
    contained: theme.colors[color]?.light || theme.colors.default.light,
    outlined: 'transparent', // Outlined variant has no background color
    text: 'transparent',      // Text variant has no background color
  }[variant] || 'transparent';

  const textColor = {
    filled: theme.colors[color]?.main || theme.colors.default.dark,
    contained: theme.colors.light.light,
    outlined: theme.colors[color]?.main || theme.colors.default.main,
    text: theme.colors[color]?.main || theme.colors.default.main,
  }[variant] || theme.colors.default.main;

  const borderColor = theme.colors[color]?.main || theme.colors.default.main;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 0.5rem',
    height: chipSize,
    minWidth:chipSize,
    borderRadius: '16px',
    backgroundColor,
    color: textColor,
    fontSize: size === 'sm' ? '0.75rem' : size === 'lg' ? '1rem' : '0.875rem',
    cursor: 'pointer',
    border: variant === 'outlined' ? `1px solid ${borderColor}` : 'none',
    transition: 'background-color 0.3s ease, color 0.3s ease',
    boxSizing:'border-box',
    '&:hover':!noHover &&  {
      backgroundColor: variant === 'contained' 
        ? theme.colors[color]?.dark 
        : variant === 'filled' 
        ? rgba(theme.colors[color]?.main, 0.5) // Darken the filled color on hover
        : variant === 'outlined' 
        ? rgba(theme.colors[color]?.main, 0.25) // Darken the filled color on hover
        : rgba(theme.colors[color]?.main, 0.15),// Darken outlined and text variants
      color: variant === 'contained' ? theme.colors.light.light : textColor, // Maintain text color
    },
    ...sx,
  };
});

// Styled component for the dot
const Dot = styled.span(({ color }) => ({
  width: '8px',
  height: '8px',
  marginInlineEnd:'0.5rem',
  borderRadius: '50%',
  backgroundColor: theme.colors[color]?.main || theme.colors.default.main,
}));

export const Chip = ({ label, color, size, variant, onClick, className, sx, dot,noHover }) => {
  return (
    <ChipStyle 
      color={color} 
      size={size} 
      variant={variant} 
      onClick={onClick} 
      className={className} 
      sx={sx}
      noHover={noHover}
    >
      {dot && <Dot color={color} />} {/* Render dot if dot prop is true */}
      {label}
    </ChipStyle>
  );
};

// PropTypes for the Chip component
Chip.propTypes = {
  /** Text displayed on the chip */
  label: PropTypes.string.isRequired,
  /** Color of the chip */
  color: PropTypes.oneOf(Object.keys(theme.colors)), // Use keys of colors in the theme
  /** Size of the chip */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /** Variant of the chip */
  variant: PropTypes.oneOf(['filled', 'outlined', 'text', 'contained']),
  /** Click event handler */
  onClick: PropTypes.func,
  /** Additional CSS class name for custom styling */
  className: PropTypes.string,
  /** Custom styles using sx prop */
  sx: PropTypes.object,
  /** Optional: Render a dot next to the label */
  dot: PropTypes.bool,
  noHover: PropTypes.bool, // New prop for disabling hover effect
};

// Default props for Chip component
Chip.defaultProps = {
  color: 'default',
  size: 'md',
  variant: 'filled',
  onClick: null,
  sx: {},
  dot: false, // Dot is optional and defaults to false
  noHover: false, // Default to false, meaning hover is enabled by default
};
